if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        this.teardownRoundedMedia();
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        /* After add-to-cart, don't hand focus back to the card button (that re-opens +/-). */
        if (cartNotification && !preventFocus) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.ensureActiveMedia();
            this.setupRoundedMedia();
            this.arrangeActionsRow();
            this.decorateSubmitButton();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      ensureActiveMedia() {
        const items = this.modalContent?.querySelectorAll('.product__media-item');
        if (!items?.length) return;
        const hasActive = [...items].some((item) => item.classList.contains('is-active'));
        if (!hasActive) items[0].classList.add('is-active');
      }

      /** Put quantity + add-to-cart on one compact action row. */
      arrangeActionsRow() {
        const qty = this.modalContent?.querySelector('.product-form__quantity');
        const buttons = this.modalContent?.querySelector('.product-form__buttons');
        if (!qty || !buttons || qty.closest('.ac-qa-actions')) return;

        const row = document.createElement('div');
        row.className = 'ac-qa-actions';
        buttons.parentNode.insertBefore(row, buttons);
        row.appendChild(qty);
        row.appendChild(buttons);
      }

      /** Cart bag icon to the left of the Add to cart label. */
      decorateSubmitButton() {
        const submit = this.modalContent?.querySelector('.product-form__submit');
        const label = submit?.querySelector(':scope > span:not(.sold-out-message)');
        if (!submit || !label || submit.querySelector('.ac-qa-submit-icon')) return;

        const icon = document.createElement('span');
        icon.className = 'ac-qa-submit-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="11 5.5 19 24" fill="none" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M20.5 6.5a4.75 4.75 0 0 0-4.75 4.75v.56h-3.16l-.77 11.6a5 5 0 0 0 4.99 5.34h7.38a5 5 0 0 0 4.99-5.33l-.77-11.6h-3.16v-.57A4.75 4.75 0 0 0 20.5 6.5m3.75 5.31v-.56a3.75 3.75 0 1 0-7.5 0v.56zm-7.5 1h7.5v.56a3.75 3.75 0 1 1-7.5 0zm-1 0v.56a4.75 4.75 0 1 0 9.5 0v-.56h2.22l.71 10.67a4 4 0 0 1-3.99 4.27h-7.38a4 4 0 0 1-4-4.27l.72-10.67z"/></svg>';
        submit.insertBefore(icon, label);
      }

      setupRoundedMedia() {
        this.teardownRoundedMedia();

        const wrapper =
          this.modalContent?.querySelector('.product__media-wrapper') ||
          this.modalContent?.querySelector('.product__media-list')?.closest('.grid__item');
        const gallery =
          wrapper?.querySelector('media-gallery') || this.modalContent?.querySelector('media-gallery');
        if (!wrapper || !gallery) return;

        gallery.classList.add('ac-qa-media-source');
        gallery.setAttribute('aria-hidden', 'true');

        wrapper.querySelectorAll('.ac-qa-media-frame').forEach((el) => el.remove());

        const frame = document.createElement('div');
        frame.className = 'ac-qa-media-frame';

        const createLayer = (active) => {
          const img = document.createElement('img');
          img.className = active ? 'ac-qa-media-frame__img is-active' : 'ac-qa-media-frame__img';
          img.alt = '';
          img.decoding = 'async';
          return img;
        };

        let front = createLayer(true);
        let back = createLayer(false);
        frame.append(front, back);
        wrapper.insertBefore(frame, gallery);

        let currentKey = '';
        let loadToken = 0;
        let syncTimer = 0;

        const mediaFromImg = (sourceImg) => {
          if (!sourceImg) return null;
          const src = sourceImg.getAttribute('src') || sourceImg.currentSrc || sourceImg.src || '';
          const srcset = sourceImg.getAttribute('srcset') || '';
          if (!src && !srcset) return null;
          return {
            src,
            srcset,
            sizes: '(min-width: 750px) 36rem, 90vw',
            alt: sourceImg.alt || '',
            key: `${srcset}||${src}`,
          };
        };

        const mediaFromGallery = () =>
          mediaFromImg(
            gallery.querySelector('.product__media-item.is-active img') ||
              gallery.querySelector('.product__media-item img') ||
              gallery.querySelector('img')
          );

        const mediaFromVariantEvent = (event) => {
          const html = event?.data?.html;
          const mediaId = event?.data?.variant?.featured_media?.id;
          if (!html || !mediaId) return null;
          const item =
            html.querySelector(`[data-media-id$="-${mediaId}"]`) ||
            html.querySelector(`[data-media-id*="${mediaId}"]`);
          return mediaFromImg(item?.querySelector('img'));
        };

        const applyAttrs = (target, media) => {
          if (media.srcset) target.setAttribute('srcset', media.srcset);
          else target.removeAttribute('srcset');
          target.setAttribute('sizes', media.sizes);
          target.alt = media.alt || '';
          if (media.src) target.setAttribute('src', media.src);
        };

        const showMedia = (media) => {
          if (!media?.key || media.key === currentKey) return;

          const token = ++loadToken;

          // Preload off-DOM so we never blank the visible layer.
          const preload = new Image();
          preload.decoding = 'async';

          const commit = () => {
            if (token !== loadToken) return;

            applyAttrs(back, media);
            // Double rAF: ensure the new bitmap is painted at opacity 0 before fade-in.
            requestAnimationFrame(() => {
              if (token !== loadToken) return;
              requestAnimationFrame(() => {
                if (token !== loadToken) return;
                back.classList.add('is-active');
                front.classList.remove('is-active');
                const prev = front;
                front = back;
                back = prev;
                currentKey = media.key;
              });
            });
          };

          preload.onload = commit;
          preload.onerror = () => {
            // Fall back to gallery image if the HTML candidate failed.
            if (token !== loadToken) return;
            const fallback = mediaFromGallery();
            if (fallback && fallback.key !== media.key) {
              showMedia(fallback);
              return;
            }
            commit();
          };

          if (media.srcset) preload.srcset = media.srcset;
          preload.sizes = media.sizes;
          if (media.src) preload.src = media.src;
          else if (media.srcset) preload.src = media.srcset.split(/\s*,\s*/)[0].split(/\s+/)[0];
        };

        const sync = (event) => {
          const next = mediaFromVariantEvent(event) || mediaFromGallery();
          if (next) showMedia(next);
        };

        const scheduleSync = () => {
          window.clearTimeout(syncTimer);
          syncTimer = window.setTimeout(() => sync(), 40);
        };

        sync();

        this._mediaObserver = new MutationObserver(scheduleSync);
        this._mediaObserver.observe(gallery, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['class', 'src', 'srcset'],
        });

        this._mediaSyncTimerClear = () => window.clearTimeout(syncTimer);

        if (typeof subscribe === 'function') {
          const productInfo = this.modalContent?.querySelector('product-info');
          this._mediaUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const sectionId = productInfo?.dataset?.originalSection || productInfo?.dataset?.section;
            if (event?.data?.sectionId && sectionId && event.data.sectionId !== sectionId) return;
            sync(event);
          });
        }
      }

      teardownRoundedMedia() {
        this._mediaObserver?.disconnect();
        this._mediaObserver = null;
        this._mediaSyncTimerClear?.();
        this._mediaSyncTimerClear = null;
        if (this._mediaUnsub) {
          this._mediaUnsub();
          this._mediaUnsub = null;
        }
        this.modalContent?.querySelectorAll('.ac-qa-media-frame').forEach((el) => el.remove());
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Keep the quick-add popup minimal — media + tags + options + qty + add
        productElement
          .querySelectorAll(
            [
              '.product__tax',
              '.product__description',
              '.product__accordion',
              '.icon-with-text',
              '.share-button',
              '.product__inventory',
              '.product__sku',
              '.volume-pricing-note',
              '.quantity__rules',
              'volume-pricing',
              '.recipient-form',
              '.shopify-payment-button',
              'product-recommendations',
              '.complementary-products',
              '.product__view-details',
              '.thumbnail-list',
              '.thumbnail-slider',
              '.slider-buttons',
              'media-gallery .slider-button',
              '.product-popup-modal__opener',
              'noscript',
            ].join(',')
          )
          .forEach((el) => el.remove());

        // Drop section vertical padding utilities (e.g. section-xxx-padding)
        [...productElement.classList].forEach((cls) => {
          if (cls.includes('padding') || cls === 'gradient') productElement.classList.remove(cls);
        });
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
